import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { getBoardData, JiraBoardData } from '../services/jiraService';

interface JiraBoardProps {
  projectId: string;
}

export function JiraBoard({ projectId }: JiraBoardProps): React.ReactElement {
  const { token } = useAuth();
  const [boardData, setBoardData] = useState<JiraBoardData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadBoardData();
  }, [projectId]);

  const loadBoardData = async () => {
    if (!token) return;

    try {
      setIsLoading(true);
      setError('');
      const data = await getBoardData(projectId, token);
      setBoardData(data);
    } catch (err: any) {
      console.error('Error loading JIRA board:', err);
      setError(err.response?.data?.error || 'Failed to load JIRA board');
    } finally {
      setIsLoading(false);
    }
  };

  const getPriorityColor = (priority: string) => {
    const priorityLower = priority?.toLowerCase() || '';
    if (priorityLower.includes('critical') || priorityLower.includes('highest')) {
      return 'bg-red-100 text-red-800 border-red-200';
    } else if (priorityLower.includes('high')) {
      return 'bg-orange-100 text-orange-800 border-orange-200';
    } else if (priorityLower.includes('medium')) {
      return 'bg-blue-100 text-blue-800 border-blue-200';
    } else if (priorityLower.includes('low') || priorityLower.includes('lowest')) {
      return 'bg-green-100 text-green-800 border-green-200';
    }
    return 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const getStatusColor = (status: string) => {
    const statusLower = status?.toLowerCase() || '';
    if (statusLower.includes('done') || statusLower.includes('complete')) {
      return 'bg-green-100 text-green-800';
    } else if (statusLower.includes('progress') || statusLower.includes('dev')) {
      return 'bg-blue-100 text-blue-800';
    } else if (statusLower.includes('review') || statusLower.includes('qa')) {
      return 'bg-purple-100 text-purple-800';
    } else if (statusLower.includes('blocked') || statusLower.includes('hold')) {
      return 'bg-red-100 text-red-800';
    }
    return 'bg-gray-100 text-gray-800';
  };

  if (isLoading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">JIRA Board</h2>
        <div className="flex items-center justify-center py-12">
          <div className="text-gray-500 dark:text-gray-400 dark:text-gray-500">Loading board data...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">JIRA Board</h2>
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-12 text-center">
          <svg className="w-16 h-16 text-gray-400 dark:text-gray-500 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Unable to Load Board</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 dark:text-gray-500 mb-4">{error}</p>
          <button
            onClick={loadBoardData}
            className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-full text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 hover:border-gray-400 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!boardData) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">JIRA Board</h2>
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-12 text-center">
          <svg className="w-16 h-16 text-gray-400 dark:text-gray-500 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
          </svg>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No Board Data</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 dark:text-gray-500">No JIRA board data available for this project.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
      {/* Board Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">{boardData.boardName}</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 dark:text-gray-500 mt-1">
              Project: <span className="font-medium text-gray-700 dark:text-gray-300">{boardData.projectKey}</span>
              {' • '}
              {boardData.totalIssues} {boardData.totalIssues === 1 ? 'issue' : 'issues'}
            </p>
          </div>
          <button
            onClick={loadBoardData}
            className="inline-flex items-center gap-2 px-3 py-1.5 bg-white border border-gray-300 rounded-full text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 hover:border-gray-400 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh
          </button>
        </div>
      </div>

      {/* Kanban Board */}
      <div className="overflow-x-auto">
        <div className="inline-flex gap-4 min-w-full">
          {boardData.columns.map((column) => {
            const issues = boardData.issuesByStatus[column] || [];
            return (
              <div
                key={column}
                className="flex-shrink-0 w-80 bg-gray-50 dark:bg-gray-700 rounded-lg p-4"
              >
                {/* Column Header */}
                <div className="mb-4">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-gray-900 dark:text-white">{column}</h3>
                    <span className="text-sm text-gray-500 dark:text-gray-400 dark:text-gray-500">
                      {issues.length}
                    </span>
                  </div>
                </div>

                {/* Issue Cards */}
                <div className="space-y-3">
                  {issues.length === 0 ? (
                    <div className="text-center py-8 text-sm text-gray-400 dark:text-gray-500">
                      No issues
                    </div>
                  ) : (
                    issues.map((issue) => (
                      <div
                        key={issue.key}
                        className="bg-white dark:bg-gray-800 rounded-lg p-3 shadow-sm hover:shadow-md transition-shadow cursor-pointer border border-gray-200"
                      >
                        {/* Issue Key */}
                        <div className="flex items-center justify-between mb-2">
                          <a
                            href={`${new URL(issue.self).origin}/browse/${issue.key}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs font-medium text-blue-600 hover:text-blue-800 hover:underline"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {issue.key}
                          </a>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${getPriorityColor(issue.fields.priority?.name || '')}`}>
                            {issue.fields.priority?.name || 'N/A'}
                          </span>
                        </div>

                        {/* Issue Summary */}
                        <p className="text-sm text-gray-900 dark:text-white mb-2 line-clamp-3">
                          {issue.fields.summary}
                        </p>

                        {/* Issue Type & Status */}
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 dark:text-gray-300 border border-gray-200">
                            {issue.fields.issuetype?.name || 'N/A'}
                          </span>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${getStatusColor(issue.fields.status?.name || '')}`}>
                            {issue.fields.status?.name || 'N/A'}
                          </span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
